import { yupResolver } from '@hookform/resolvers/yup';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { AvailableRecordTypeFragment } from '../../../__generated__/graphql';
import { ExtraFieldInternalValue } from '../extraFieldInternalValue';
import {
  collectFormDataExtraFields,
  getFormValuesFromExtraFields,
  recordTypeFormKey,
} from '../logic';

type ExtraFieldsFormValues = Record<string, unknown>;

export type Props = {
  onChange: (
    extraFields: ExtraFieldInternalValue[],
    recordTypeName: string | undefined,
  ) => void;
  values?: readonly ExtraFieldInternalValue[];
  recordTypeName: string | null;
  availableRecordTypes: readonly AvailableRecordTypeFragment[];
  validationSchema?: yup.AnyObjectSchema;
  inputsMounted: boolean;
};

export const useExtraFieldsFormSimple = ({
  values,
  recordTypeName,
  onChange,
  validationSchema,
  availableRecordTypes,
  inputsMounted,
}: Props) => {
  const defaultRecordTypeName = useMemo(
    () =>
      availableRecordTypes.find((recordType) => recordType.IsDefault)
        ?.DeveloperName ?? 'Master',
    [availableRecordTypes],
  );
  const formValues = useMemo(
    () =>
      getFormValuesFromExtraFields(
        values ?? [],
        recordTypeName ?? defaultRecordTypeName,
      ),
    [defaultRecordTypeName, recordTypeName, values],
  );
  const initialFormValues = useRef<ExtraFieldsFormValues>(formValues);
  const resettingToValues = useRef<ExtraFieldsFormValues>();

  const formMethods = useForm<ExtraFieldsFormValues>({
    defaultValues: initialFormValues.current,
    resolver: validationSchema ? yupResolver(validationSchema) : undefined,
    shouldUnregister: true,
  });

  const { watch, reset } = formMethods;
  const watchedValues = watch();

  useEffect(() => {
    if (!isEqual(formValues, initialFormValues.current)) {
      initialFormValues.current = formValues;
      resettingToValues.current = formValues;
      reset(formValues);
    }
  }, [reset, formValues]);

  useEffect(() => {
    if (!inputsMounted) {
      return;
    }
    if (
      resettingToValues.current !== undefined &&
      !isEqual(watchedValues, resettingToValues.current)
    ) {
      return;
    }
    resettingToValues.current = undefined;
    if (!isEqual(watchedValues, formValues)) {
      initialFormValues.current = watchedValues;
      const extraFieldInputs = collectFormDataExtraFields(watchedValues);
      const selectedRecordTypeName = watchedValues[recordTypeFormKey];
      onChange(
        extraFieldInputs,
        typeof selectedRecordTypeName === 'string'
          ? selectedRecordTypeName
          : undefined,
      );
    }
  }, [watchedValues, onChange, formValues, inputsMounted]);

  return { formMethods };
};
